/**
 * ApiKeyPrompt - Modal dialog for entering OpenRouter API key.
 *
 * Shown when player first interacts with an AI chat NPC and no key
 * is found in env or localStorage. Key is stored for future sessions.
 */
export default class ApiKeyPrompt {
  /**
   * Show the API key entry modal.
   * @returns {Promise<string|null>} The entered key, or null if cancelled
   */
  static show() {
    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.className = 'chat-apikey-overlay';

      const modal = document.createElement('div');
      modal.className = 'chat-apikey-modal';

      const title = document.createElement('div');
      title.className = 'chat-apikey-title';
      title.textContent = 'AI Chat — API Key';

      const desc = document.createElement('div');
      desc.className = 'chat-apikey-desc';
      desc.innerHTML = 'To chat with AI NPCs, enter your free OpenRouter API key.<br><br>' +
        'Get one at <a href="https://openrouter.ai/keys" target="_blank" rel="noopener">openrouter.ai/keys</a> ' +
        '(no credit card needed).';

      const input = document.createElement('input');
      input.className = 'chat-apikey-input';
      input.type = 'password';
      input.placeholder = 'sk-or-v1-...';
      input.autocomplete = 'off';
      input.spellcheck = false;

      const actions = document.createElement('div');
      actions.className = 'chat-apikey-actions';

      const cancelBtn = document.createElement('button');
      cancelBtn.className = 'chat-apikey-cancel';
      cancelBtn.textContent = 'Cancel';

      const saveBtn = document.createElement('button');
      saveBtn.className = 'chat-apikey-save';
      saveBtn.textContent = 'Save';

      actions.appendChild(cancelBtn);
      actions.appendChild(saveBtn);

      modal.appendChild(title);
      modal.appendChild(desc);
      modal.appendChild(input);
      modal.appendChild(actions);
      overlay.appendChild(modal);
      document.body.appendChild(overlay);

      const cleanup = () => {
        overlay.remove();
        window.removeEventListener('keydown', onKeyDown, true);
      };

      const save = () => {
        const key = input.value.trim();
        if (!key) return;
        cleanup();
        resolve(key);
      };

      const cancel = () => {
        cleanup();
        resolve(null);
      };

      const onKeyDown = (e) => {
        e.stopPropagation();
        if (e.key === 'Enter') {
          e.preventDefault();
          save();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          cancel();
        }
      };

      saveBtn.addEventListener('click', save);
      cancelBtn.addEventListener('click', cancel);
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) cancel();
      });
      window.addEventListener('keydown', onKeyDown, true);

      // Auto-focus input
      requestAnimationFrame(() => input.focus());
    });
  }
}
